import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { PageToolbar, FilterChip } from '@/components/ui/page-toolbar';
import { DataTable } from '@/components/ui/data-table';
import { FloatingField } from '@/components/ui/floating-field';
import { MoneyField } from '@/components/ui/money-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { LoadDocumentsPanel } from '@/components/LoadDocumentsPanel';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { LOADS } from '@/constants/testIds';

const LOAD_STATUSES = [
  'draft',
  'dispatched',
  'in_transit',
  'delivered',
  'invoiced',
  'closed',
  'canceled',
];

const emptyForm = {
  loadNumber: '',
  shipperId: '',
  consigneeId: '',
  pickup: null,
  delivery: null,
  rate: '',
  currency: 'USD',
  notes: '',
};

function toIsoNoon(date) {
  if (!date) return undefined;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return new Date(`${y}-${m}-${day}T12:00:00`).toISOString();
}

function fromIsoDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function LoadsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canMutateLoads = user?.role === 'admin' || user?.role === 'dispatcher';
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [selectedPacket, setSelectedPacket] = useState(() => searchParams.get('packet') || '');
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState('');
  const editSectionRef = useRef(null);

  useEffect(() => {
    const packet = searchParams.get('packet');
    if (packet) setSelectedPacket(packet);
  }, [searchParams]);

  const loadsQ = useQuery({
    queryKey: ['loads'],
    queryFn: async () => {
      const r = await api('/api/v1/loads');
      if (r.status !== 200) throw new Error('loads');
      return r.data.loads ?? [];
    },
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });

  const openEditLoad = (row) => {
    setShowCreate(true);
    setEditingId(row.id);
    setForm({
      loadNumber: row.loadNumber || '',
      shipperId: row.shipperId || '',
      consigneeId: row.consigneeId || '',
      pickup: fromIsoDate(row.pickupDate),
      delivery: fromIsoDate(row.deliveryDate),
      rate: row.rate != null ? String((row.rate || 0) / 100) : '',
      currency: row.currency || 'USD',
      notes: row.notes || '',
    });
    requestAnimationFrame(() => {
      editSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    if (!canMutateLoads) return;
    const highlight = searchParams.get('highlight');
    if (!highlight) return;
    const row = (loadsQ.data ?? []).find((l) => l.id === highlight);
    if (!row) return;
    openEditLoad(row);
  }, [searchParams, loadsQ.data, canMutateLoads]);

  const shippersQ = useQuery({
    queryKey: ['shippers', 'shipper'],
    queryFn: async () => {
      const r = await api('/api/v1/shippers?partyType=shipper');
      if (r.status !== 200) throw new Error('shippers');
      return r.data.shippers ?? [];
    },
  });

  const consigneesQ = useQuery({
    queryKey: ['shippers', 'consignee'],
    queryFn: async () => {
      const r = await api('/api/v1/shippers?partyType=consignee');
      if (r.status !== 200) throw new Error('consignees');
      return r.data.shippers ?? [];
    },
  });

  const createMut = useMutation({
    mutationFn: async (body) => {
      const r = await api('/api/v1/loads', { method: 'POST', body });
      if (r.status !== 201) {
        throw new Error(String(r.data?.error ?? 'Create failed'));
      }
      return r.data;
    },
    onSuccess: (data) => {
      toast.success('Load created');
      setShowCreate(false);
      setForm(emptyForm);
      if (data?.load) {
        qc.setQueryData(['loads'], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return [data.load, ...list.filter((l) => l.id !== data.load.id)];
        });
        setSelectedPacket(data.load.id);
      } else {
        qc.invalidateQueries({ queryKey: ['loads'] });
      }
    },
    onError: (err) => toast.error(err.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => {
      const r = await api(`/api/v1/loads/${id}`, { method: 'PATCH', body });
      if (r.status !== 200) {
        throw new Error(String(r.data?.error ?? 'Update failed'));
      }
      return r.data;
    },
    onSuccess: (data) => {
      toast.success('Load updated');
      setEditingId('');
      if (data?.load) {
        qc.setQueryData(['loads'], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((l) => (l.id === data.load.id ? data.load : l));
        });
      } else {
        qc.invalidateQueries({ queryKey: ['loads'] });
      }
    },
    onError: (err) => toast.error(err.message || 'Update failed'),
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => {
      const r = await api(`/api/v1/loads/${id}/status`, {
        method: 'POST',
        body: { status },
      });
      if (r.status !== 200) {
        throw new Error(String(r.data?.error ?? 'Status update failed'));
      }
      return r.data;
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['loads'] });
      const previous = qc.getQueryData(['loads']);
      qc.setQueryData(['loads'], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((l) =>
          l.id === id ? { ...l, status, updatedAt: new Date().toISOString() } : l
        );
      });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['loads'], ctx.previous);
      toast.error(err.message || 'Status update failed');
    },
    onSuccess: (data) => {
      toast.success('Status updated');
      if (data?.load) {
        qc.setQueryData(['loads'], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((l) => (l.id === data.load.id ? { ...l, ...data.load } : l));
        });
      }
      qc.invalidateQueries({ queryKey: ['loads'] });
      qc.invalidateQueries({ queryKey: ['dispatch-board'] });
      qc.invalidateQueries({ queryKey: ['overview-stats'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => {
      const r = await api(`/api/v1/loads/${id}`, { method: 'DELETE' });
      if (r.status !== 200) {
        throw new Error(String(r.data?.error ?? 'Delete failed'));
      }
      return id;
    },
    onSuccess: (id) => {
      toast.success('Load deleted');
      setDeleteId('');
      setSelectedPacket('');
      qc.setQueryData(['loads'], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.filter((l) => l.id !== id);
      });
      qc.invalidateQueries({ queryKey: ['overview-stats'] });
    },
    onError: (err) => toast.error(err.message || 'Delete failed'),
  });

  const loads = useMemo(() => {
    const list = [...(loadsQ.data ?? [])];
    list.sort((a, b) => {
      const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return tb - ta;
    });
    return list;
  }, [loadsQ.data]);
  const shippers = shippersQ.data ?? [];
  const consignees = consigneesQ.data ?? [];
  const editing = loads.find((l) => l.id === editingId);
  const deleteTarget = loads.find((l) => l.id === deleteId);

  const partyName = useMemo(() => {
    const map = new Map();
    for (const p of [...shippers, ...consignees]) map.set(p.id, p.name);
    return (id) => map.get(id) || '—';
  }, [shippers, consignees]);

  const statuses = useMemo(
    () => ['all', ...Array.from(new Set(loads.map((l) => l.status).filter(Boolean)))],
    [loads]
  );

  const filteredLoads =
    statusFilter === 'all' ? loads : loads.filter((load) => load.status === statusFilter);

  const tableRows = filteredLoads.map((l) => ({
    ...l,
    shipper: l.shipperName || partyName(l.shipperId),
    consignee: l.consigneeName || partyName(l.consigneeId),
  }));

  const columns = [
    {
      key: 'loadNumber',
      label: 'Load #',
      sortable: true,
      className: 'font-mono',
    },
    {
      key: 'status',
      label: 'Status',
      render: (v, row) => {
        const status = v || 'draft';
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <StatusPill status={status} />
            {canMutateLoads ? (
              <Select
                key={`${row.id}-${status}`}
                value={status}
                onValueChange={(next) => {
                  if (next === status) return;
                  statusMut.mutate({ id: row.id, status: next });
                }}
                disabled={statusMut.isPending}
              >
                <SelectTrigger
                  className="h-8 w-[120px]"
                  data-testid={`load-status-${row.id}`}
                >
                  <SelectValue placeholder="Change" />
                </SelectTrigger>
                <SelectContent>
                  {LOAD_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
        );
      },
    },
    { key: 'shipper', label: 'Shipper', sortable: true },
    { key: 'consignee', label: 'Consignee', sortable: true },
    {
      key: 'pickupDate',
      label: 'Pickup',
      sortable: true,
      render: (v) => formatDate(v),
    },
    {
      key: 'deliveryDate',
      label: 'Delivery',
      sortable: true,
      render: (v) => formatDate(v),
    },
    {
      key: 'rate',
      label: 'Rate',
      sortable: true,
      render: (v, row) => (
        <span className="font-mono">{formatCurrency((v || 0) / 100, row.currency || 'USD')}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-end">
          {canMutateLoads ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditLoad(row);
                }}
                data-testid={`edit-load-${row.id}`}
              >
                <Icons.Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(row.id);
                }}
                data-testid={`delete-load-${row.id}`}
              >
                <Icons.Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </>
          ) : null}
          <Button
            size="sm"
            variant={selectedPacket === row.id ? 'default' : 'outline'}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPacket(selectedPacket === row.id ? '' : row.id);
            }}
            data-testid={`packet-${row.id}`}
          >
            <Icons.Paperclip className="h-3 w-3 mr-1" />
            Packet
          </Button>
        </div>
      ),
    },
  ];

  const handleCreate = () => {
    if (!form.loadNumber.trim() || !form.shipperId || !form.consigneeId || !form.rate) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!form.pickup || !form.delivery) {
      toast.error('Pickup and delivery dates are required');
      return;
    }
    if (new Date(form.delivery) < new Date(form.pickup)) {
      toast.error('Delivery date must be on or after pickup date');
      return;
    }
    const rateCents = Math.round(parseFloat(form.rate) * 100);
    if (!Number.isFinite(rateCents) || rateCents <= 0) {
      toast.error('Enter a valid positive rate');
      return;
    }
    createMut.mutate({
      loadNumber: form.loadNumber.trim(),
      shipperId: form.shipperId,
      consigneeId: form.consigneeId,
      pickupDate: toIsoNoon(form.pickup),
      deliveryDate: toIsoNoon(form.delivery),
      rate: rateCents,
      currency: form.currency,
      notes: form.notes || undefined,
    });
  };

  const handleUpdate = () => {
    if (!editingId) return;
    if (!form.shipperId || !form.consigneeId || !form.rate) {
      toast.error('Shipper, consignee, and rate are required');
      return;
    }
    if (!form.pickup || !form.delivery) {
      toast.error('Pickup and delivery dates are required');
      return;
    }
    if (new Date(form.delivery) < new Date(form.pickup)) {
      toast.error('Delivery date must be on or after pickup date');
      return;
    }
    const rateCents = Math.round(parseFloat(form.rate) * 100);
    if (!Number.isFinite(rateCents) || rateCents <= 0) {
      toast.error('Enter a valid positive rate');
      return;
    }
    updateMut.mutate({
      id: editingId,
      body: {
        shipperId: form.shipperId,
        consigneeId: form.consigneeId,
        pickupDate: toIsoNoon(form.pickup),
        deliveryDate: toIsoNoon(form.delivery),
        rate: rateCents,
        currency: form.currency,
        notes: form.notes || undefined,
      },
    });
  };

  const selectedLoad = loads.find((l) => l.id === selectedPacket);

  function renderLoadFields(values, setValues, { showLoadNumber }) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showLoadNumber ? (
            <FloatingField
              label="Load Number"
              value={values.loadNumber}
              onChange={(e) => setValues({ ...values, loadNumber: e.target.value })}
              required
              data-testid="load-number-input"
            />
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Load Number
              </p>
              <p className="font-mono text-sm h-10 flex items-center">{values.loadNumber || '—'}</p>
            </div>
          )}
          <MoneyField
            label="Rate (USD)"
            value={values.rate}
            onChange={(e) => setValues({ ...values, rate: e.target.value })}
            required
            data-testid="load-rate-input"
          />
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Shipper <span className="text-destructive">*</span>
            </label>
            <Combobox
              options={shippers.map((p) => ({ value: p.id, label: p.name }))}
              value={values.shipperId}
              onChange={(v) => setValues({ ...values, shipperId: v })}
              placeholder="Select shipper"
              allowClear
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Consignee <span className="text-destructive">*</span>
            </label>
            <Combobox
              options={consignees.map((p) => ({ value: p.id, label: p.name }))}
              value={values.consigneeId}
              onChange={(v) => setValues({ ...values, consigneeId: v })}
              placeholder="Select consignee"
              allowClear
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Pickup Date
            </label>
            <DateField
              value={values.pickup}
              onChange={(d) => {
                const next = { ...values, pickup: d };
                if (next.delivery && d && new Date(next.delivery) < new Date(d)) {
                  next.delivery = d;
                }
                setValues(next);
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Delivery Date
            </label>
            <DateField
              value={values.delivery}
              onChange={(d) => setValues({ ...values, delivery: d })}
              minDate={values.pickup || undefined}
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Currency
            </label>
            <Select
              value={values.currency}
              onValueChange={(v) => setValues({ ...values, currency: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD - US Dollar</SelectItem>
                <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                <SelectItem value="MXN">MXN - Mexican Peso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Notes
          </label>
          <textarea
            value={values.notes}
            onChange={(e) => setValues({ ...values, notes: e.target.value })}
            placeholder="Special instructions, references, etc."
            rows={3}
            className="mt-2 w-full px-3 py-2 border rounded-md text-sm resize-none outline-none focus:ring-2 focus:ring-ring bg-background"
          />
        </div>
      </div>
    );
  }

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Loads"
          description="Manage shipments and freight operations"
          icon="Package"
          metrics={[
            { label: 'Total', value: loads.length },
            {
              label: 'In Transit',
              value: loads.filter((l) => l.status === 'in_transit').length,
            },
            { label: 'Draft', value: loads.filter((l) => l.status === 'draft').length },
          ]}
          actions={
            canMutateLoads ? (
              <Button
                data-testid={LOADS.createButton}
                onClick={() => {
                  setEditingId('');
                  setForm(emptyForm);
                  setShowCreate(!showCreate || !!editingId);
                }}
              >
                <Icons.Plus className="h-4 w-4 mr-2" />
                Create Load
              </Button>
            ) : null
          }
        />

        {showCreate && canMutateLoads && (
          <Card ref={editSectionRef}>
            <CardHeader>
              <CardTitle>{editingId ? `Edit Load ${form.loadNumber || ''}`.trim() : 'New Load'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderLoadFields(form, setForm, { showLoadNumber: !editingId })}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setEditingId('');
                    setForm(emptyForm);
                  }}
                >
                  Cancel
                </Button>
                {editingId ? (
                  <Button onClick={handleUpdate} disabled={updateMut.isPending}>
                    {updateMut.isPending ? 'Saving…' : 'Save Changes'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreate}
                    disabled={createMut.isPending}
                    data-testid="save-load-button"
                  >
                    {createMut.isPending ? 'Creating…' : 'Create Load'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <PageToolbar>
          {statuses.map((status) => (
            <FilterChip
              key={status}
              active={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              data-testid={LOADS.statusFilter(status)}
            >
              {status === 'all' ? 'All' : status.replace(/_/g, ' ')}
            </FilterChip>
          ))}
        </PageToolbar>

        {loadsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading loads…</p>
        ) : loadsQ.isError ? (
          <p className="text-sm text-destructive">Could not load loads from API.</p>
        ) : (
          <DataTable
            data-testid={LOADS.table}
            columns={columns}
            data={tableRows}
            emptyMessage="No loads yet"
          />
        )}

        {selectedLoad ? (
          <LoadDocumentsPanel loadId={selectedLoad.id} loadNumber={selectedLoad.loadNumber} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Select <strong>Packet</strong> on a load to upload BOL, POD, and fuel receipts.
          </p>
        )}

        <ConfirmDialog
          open={Boolean(deleteId)}
          onOpenChange={(open) => !open && setDeleteId('')}
          title="Delete load?"
          description={`Permanently remove load ${deleteTarget?.loadNumber || ''}? Prefer canceling status if it already moved freight.`}
          confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete'}
          variant="destructive"
          onConfirm={() => deleteMut.mutate(deleteId)}
        />
      </div>
);
}
